import { toUserMessage } from '../../lib/errors.js';
import { confirmDialog } from '../../lib/ui/dialogs.js';
import {
  IonAlert,
  IonButton,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonPage,
  IonProgressBar,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTitle,
  IonToast,
  IonToggle,
  IonToolbar,
} from '@ionic/react';
import {
  appsOutline,
  businessOutline,
  cameraOutline,
  lockClosedOutline,
  logOutOutline,
  optionsOutline,
} from 'ionicons/icons';
import { useEffect, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { canManageTenantFeatures } from '../../lib/auth/capabilities.js';
import { useSharedTabletOperator } from '../../lib/auth/SharedTabletOperatorProvider.js';
import { shareWithNavigator } from '../../lib/adapters/share/index.js';
import {
  checkForUpdate,
  downloadAndApplyUpdate,
  getCurrentVersion,
  restartAppForUpdate,
  type UpdateCheckResult,
} from '../../lib/updater.js';
import {
  useMasters,
  useSetCollettivaEnabled,
  useSetWarehouseEnabled,
  useCollettivaEnabled,
  useUserTenants,
  useWarehouseEnabled,
  useRecipeQuantitiesEnabled,
  useSetRecipeQuantitiesEnabled,
  useFoodCostEnabled,
  useSetFoodCostEnabled,
  useEventQuotesEnabled,
  useSetEventQuotesEnabled,
  useProductionLotPhotoEnabled,
  useSetProductionLotPhotoEnabled,
  useTransportDocumentsEnabled,
  useSetTransportDocumentsEnabled,
  useAiAgentEnabled,
  useSetAiAgentEnabled,
  useReceivingTempPerItemEnabled,
  useSetReceivingTempPerItemEnabled,
  useDefaultVatRate,
  useSetDefaultVatRate,
  useAccountAccessState,
  useDashboardConfigState,
} from '../../lib/data/queries.js';
import { isSurfaceVisibleForSubjects } from '../../lib/visibility/isSurfaceVisible.js';
import { setLanguage } from '../../lib/i18n/index.js';
import { getSupabase } from '../../lib/supabase.js';
import { useSyncState } from '../../lib/sync/SyncProvider.js';
import { ShareAppCard } from './ShareAppCard.js';
import { useCelebrate } from '../../components/CelebrationToast.js';
import { ComplianceDocModal } from '../../components/ComplianceDocModal.js';
import { useOperationalIdentity } from '../../lib/auth/useOperationalIdentity.js';

interface BusinessRow {
  id: string;
  tenant_id: string;
  name: string;
  city?: string | null;
  archived_at?: string | null;
}

type ConsultantSettingsSection = 'modules' | 'client' | 'app';

export function SettingsPage(): JSX.Element {
  const isNative = Capacitor.isNativePlatform();
  const { t, i18n } = useTranslation();
  const { profile, user, switchTenant, switchBusiness } = useAuth();
  const sharedTabletOperator = useSharedTabletOperator();
  const sync = useSyncState();
  const { data: businesses = [] } = useMasters<BusinessRow>('businesses');
  // T-062: nomi reali dei tenant nel dropdown "Azienda" (prima mostrava i primi
  // 8 char dell'UUID, es. "94f588eb", invisibile e inutile per l'utente).
  // FONTI in cascata:
  //   1) localTenants — `tenants` è già nella MASTER_TABLES di sync-pull
  //      (offline-first, sempre fresco al cold start dopo la prima sync).
  //   2) remoteTenants — fallback online via useUserTenants(): copre il caso
  //      di prima sync mai avvenuta o cache locale vuota.
  //   3) remoteTenantsState — fetch diretto ulteriore, fired in useEffect:
  //      garantisce che anche se React Query tiene initialData stale forever
  //      (es. errori di rete intermittenti) almeno una volta il name venga
  //      letto e messo nella map. Belt + suspenders sul bug "Ristorante 94f588eb".
  const { data: localTenants = [] } = useMasters<{ id: string; name: string }>('tenants');
  const { data: remoteTenants = [] } = useUserTenants();
  const [remoteTenantsState, setRemoteTenantsState] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    const ids = profile?.tenants ?? [];
    if (ids.length === 0) return;
    let cancelled = false;
    void (async () => {
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('id,name')
        .in('id', ids);
      if (cancelled || error || !data) return;
      setRemoteTenantsState(data as Array<{ id: string; name: string }>);
    })();
    return () => { cancelled = true; };
  }, [profile?.tenants?.join(',')]);
  const tenantNameById = useMemo(() => {
    const m = new Map<string, string>();
    // Ordine: locale → remoto (queryHook) → remoto (useEffect diretto).
    // L'ultimo a scrivere vince — preferiamo il fetch più recente. Tutti e tre
    // restano allineati perché leggono dalla stessa tabella `tenants`.
    for (const r of localTenants) {
      const name = (r.name ?? '').trim();
      if (name) m.set(r.id, name);
    }
    for (const r of remoteTenants) {
      const name = (r.name ?? '').trim();
      if (name) m.set(r.id, name);
    }
    for (const r of remoteTenantsState) {
      const name = (r.name ?? '').trim();
      if (name) m.set(r.id, name);
    }
    return m;
  }, [localTenants, remoteTenants, remoteTenantsState]);
  // Esclude i locali archiviati (soft-deleted): restano nel DB per audit HACCP
  // ma non devono apparire nello switcher né nelle altre viste utente.
  // T-403: ordinate per ZONA (city) e poi per nome — le sedi della stessa zona
  // (es. Stabilimento + Magazzino Favara) compaiono adiacenti nello switcher.
  const localTenantBusinesses = useMemo(
    () => businesses
      .filter((b) => b.tenant_id === profile?.active_tenant_id && !b.archived_at)
      .sort((a, b) => ((a.city ?? '').localeCompare(b.city ?? '', 'it')) || a.name.localeCompare(b.name, 'it')),
    [businesses, profile?.active_tenant_id],
  );
  const [remoteBusinesses, setRemoteBusinesses] = useState<BusinessRow[]>([]);
  useEffect(() => {
    if (!profile?.active_tenant_id) return;
    if (localTenantBusinesses.length > 0) return;
    let cancelled = false;
    void (async () => {
      const { data } = await getSupabase()
        .from('businesses')
        .select('id,tenant_id,name,city,archived_at')
        .eq('tenant_id', profile.active_tenant_id)
        .is('archived_at', null)
        .order('city', { ascending: true })
        .order('name', { ascending: true });
      if (!cancelled && data) setRemoteBusinesses(data as BusinessRow[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [profile?.active_tenant_id, localTenantBusinesses.length]);
  const tenantBusinesses =
    localTenantBusinesses.length > 0 ? localTenantBusinesses : remoteBusinesses;
  const [newBusinessName, setNewBusinessName] = useState('');
  const [creatingBusiness, setCreatingBusiness] = useState(false);
  // T-062: crea ristorante demo (nuovo tenant + business) — solo platform owner.
  // Un solo input perché tenant + business hanno lo stesso "nome placeholder"
  // (ambiente vergine che poi il ristoratore personalizza una volta registrato).
  const [demoTenantName, setDemoTenantName] = useState('');
  const [creatingDemoTenant, setCreatingDemoTenant] = useState(false);
  const [lastCreatedDemoTenantId, setLastCreatedDemoTenantId] = useState<string | null>(null);
  // T-063: invita ristoratore via email su un tenant esistente (riusa
  // invite-staff edge function con app_role='ristoratore'). Il consulente
  // sceglie a quale tenant invitare e l'app genera l'URL da incollare in
  // WhatsApp / SMS / email al cliente.
  const [inviteTargetTenant, setInviteTargetTenant] = useState<string>('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteCustomerName, setInviteCustomerName] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviteEmailSent, setInviteEmailSent] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [complianceOpen, setComplianceOpen] = useState(false);
  const [consultantSection, setConsultantSection] =
    useState<ConsultantSettingsSection>('modules');

  const [updateCheck, setUpdateCheck] = useState<UpdateCheckResult | null>(null);
  const [updateChecking, setUpdateChecking] = useState(false);
  const [updateProgress, setUpdateProgress] = useState<number | null>(null);
  // T-017 follow-up: la versione installata DEVE essere visibile fin dal primo
  // render della pagina, anche prima di premere "Verifica". `updateCheck` si
  // popola solo dopo il network call; qui invece carichiamo il bundle locale
  // (LiveUpdate.getBundle su nativo, VITE_APP_VERSION su web) al mount.
  const [installedVersion, setInstalledVersion] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void getCurrentVersion().then((v) => {
      if (!cancelled) setInstalledVersion(v);
    }).catch(() => {
      // Fallback: lasciamo `installedVersion` null, il render mostra "—"
      // ma il pulsante "Verifica" rimane attivo per riprovare.
    });
    return () => { cancelled = true; };
  }, []);

  const warehouseEnabled = useWarehouseEnabled();
  const setWarehouseEnabled = useSetWarehouseEnabled();
  const togglingWarehouse = setWarehouseEnabled.isPending;
  // T-215: modulo ristorazione collettiva (scheda produzione, DDT, destinazioni).
  const collettivaEnabled = useCollettivaEnabled();
  const setCollettivaEnabled = useSetCollettivaEnabled();
  const togglingCollettiva = setCollettivaEnabled.isPending;
  // T-285: modulo Food Cost (costo ricette, food cost %, report).
  const foodCostEnabled = useFoodCostEnabled();
  const setFoodCostEnabled = useSetFoodCostEnabled();
  const togglingFoodCost = setFoodCostEnabled.isPending;
  const eventQuotesEnabled = useEventQuotesEnabled();
  const setEventQuotesEnabled = useSetEventQuotesEnabled();
  const togglingEventQuotes = setEventQuotesEnabled.isPending;
  // T-562: acquisizione foto dei lotti durante produzione/ricetta.
  const productionLotPhotoEnabled = useProductionLotPhotoEnabled();
  const setProductionLotPhotoEnabled = useSetProductionLotPhotoEnabled();
  const togglingProductionLotPhoto = setProductionLotPhotoEnabled.isPending;
  const transportDocumentsEnabled = useTransportDocumentsEnabled();
  const setTransportDocumentsEnabled = useSetTransportDocumentsEnabled();
  const togglingTransportDocuments = setTransportDocumentsEnabled.isPending;
  // T-450 F1: aliquota IVA di default del locale (scorporo food cost).
  const defaultVatRate = useDefaultVatRate();
  const setDefaultVatRate = useSetDefaultVatRate();
  const [vatDraft, setVatDraft] = useState<string | null>(null);
  // T-373: modulo Agente AI (placeholder; feature in sviluppo, T-374).
  const aiAgentEnabled = useAiAgentEnabled();
  const setAiAgentEnabled = useSetAiAgentEnabled();
  const togglingAiAgent = setAiAgentEnabled.isPending;
  // T-130: gestione quantità nelle ricette (toggleabile anche dal ristoratore).
  const recipeQtyEnabled = useRecipeQuantitiesEnabled();
  const setRecipeQtyEnabled = useSetRecipeQuantitiesEnabled();
  const togglingRecipeQty = setRecipeQtyEnabled.isPending;
  // T-291: temperatura di ricevimento PER articolo nel Carico DDT/fattura.
  const receivingTempEnabled = useReceivingTempPerItemEnabled();
  const setReceivingTempEnabled = useSetReceivingTempPerItemEnabled();
  const togglingReceivingTemp = setReceivingTempEnabled.isPending;
  // Valore in attesa di conferma (IonAlert su ogni cambio del flag).
  const [recipeQtyConfirm, setRecipeQtyConfirm] = useState<boolean | null>(null);
  const celebrate = useCelebrate();

  // Gating per ruolo (T-011). L'owner di piattaforma vede tutto; il ristoratore
  // (e qualsiasi altro non-owner) NON vede Diagnostica, Audit & Compliance e
  // la card Magazzino. La sessione di oggi è limitata a owner + ristoratore;
  // gli altri ruoli (consulente/osa/staff) ereditano il comportamento "non
  // owner" per default — verrà raffinato in un task successivo.
  const isPlatformOwner = profile?.is_platform_owner === true;
  // T-244: il control-plane "consulente" (creare/archiviare locali, creare
  // ambienti demo, invitare il ristoratore, attivare Magazzino Pro / Collettiva)
  // spetta a OGNI consulente sui PROPRI tenant — non solo al platform owner.
  // La god-view su TUTTI i tenant resta invece esclusiva dell'owner (gate su
  // isPlatformOwner altrove). I clienti (ristoratore/osa/staff) NON entrano qui.
  const isConsultantOrOwner = isPlatformOwner || profile?.global_role === 'consulente';
  const canBypassTenantVisibility = isPlatformOwner || profile?.role === 'consulente';
  const canManageFeatures = canManageTenantFeatures(profile);

  const dashboardState = useDashboardConfigState();
  const accountAccess = useAccountAccessState();
  const operationalIdentity = useOperationalIdentity();
  const dashboardConfig = dashboardState.config;
  const isSettingsItemVisible = (key: string): boolean =>
    canBypassTenantVisibility || (
      dashboardState.resolved
      && accountAccess.resolved
      && isSurfaceVisibleForSubjects(dashboardConfig, operationalIdentity.subjects, key)
    );

  async function onToggleWarehouse(next: boolean): Promise<void> {
    try {
      await setWarehouseEnabled.mutateAsync(next);
      setToast(next ? 'Magazzino attivato' : 'Magazzino disattivato');
    } catch (e) {
      setToast(toUserMessage(e));
    }
  }

  async function onToggleCollettiva(next: boolean): Promise<void> {
    try {
      await setCollettivaEnabled.mutateAsync(next);
      // T-221: il modulo collettiva presuppone il magazzino (carico prodotti
      // finiti, scarichi dei DDT). Un trigger DB lo forza comunque lato
      // server; qui allineiamo anche flag/cache client subito.
      if (next && !warehouseEnabled) {
        await setWarehouseEnabled.mutateAsync(true);
        setToast('Modulo collettiva attivato (magazzino attivato automaticamente)');
        return;
      }
      setToast(next ? 'Modulo collettiva attivato' : 'Modulo collettiva disattivato');
    } catch (e) {
      setToast(toUserMessage(e));
    }
  }

  async function onToggleFoodCost(next: boolean): Promise<void> {
    try {
      await setFoodCostEnabled.mutateAsync(next);
      // T-285: il Food Cost presuppone le quantità ricette (costo = dosi × costo
      // unitario). Un trigger DB le forza comunque server-side; qui allineiamo
      // anche flag/cache client subito (come collettiva fa col magazzino).
      if (next && !recipeQtyEnabled) {
        await setRecipeQtyEnabled.mutateAsync(true);
        setToast('Modulo Food Cost attivato (quantità ricette attivate automaticamente)');
        return;
      }
      setToast(next ? 'Modulo Food Cost attivato' : 'Modulo Food Cost disattivato');
    } catch (e) {
      setToast(toUserMessage(e));
    }
  }

  async function onToggleEventQuotes(next: boolean): Promise<void> {
    try {
      await setEventQuotesEnabled.mutateAsync(next);
      setToast(next ? 'Modulo Sale Trattenimenti / Catering attivato' : 'Modulo Sale Trattenimenti / Catering disattivato');
    } catch (e) { setToast(toUserMessage(e)); }
  }

  async function onToggleProductionLotPhoto(next: boolean): Promise<void> {
    try {
      await setProductionLotPhotoEnabled.mutateAsync(next);
      setToast(next
        ? 'Foto lotti in produzione attivate'
        : 'Foto lotti in produzione disattivate');
    } catch (e) {
      setToast(toUserMessage(e));
    }
  }

  async function onToggleTransportDocuments(next: boolean): Promise<void> {
    try {
      await setTransportDocumentsEnabled.mutateAsync(next);
      setToast(next
        ? 'Modulo Documenti di trasporto attivato'
        : 'Modulo Documenti di trasporto disattivato');
    } catch (e) { setToast(toUserMessage(e)); }
  }

  // T-450 F1: salva l'aliquota IVA di default del locale (vuoto/invalido = ignora).
  async function saveVatRate(): Promise<void> {
    if (vatDraft == null) return;
    const raw = vatDraft.trim().replace(',', '.');
    const n = Number(raw);
    setVatDraft(null);
    if (raw === '' || !Number.isFinite(n) || n < 0) return;
    if (n === defaultVatRate) return;
    try {
      await setDefaultVatRate.mutateAsync(n);
      setToast(`IVA di default impostata a ${n}%`);
    } catch (e) {
      setToast(toUserMessage(e));
    }
  }

  async function onToggleAiAgent(next: boolean): Promise<void> {
    try {
      await setAiAgentEnabled.mutateAsync(next);
      // T-373: placeholder — l'assistente conversazionale vero è in sviluppo (T-374).
      setToast(next ? 'Modulo Agente AI attivato (in sviluppo)' : 'Modulo Agente AI disattivato');
    } catch (e) {
      setToast(toUserMessage(e));
    }
  }

  async function onToggleReceivingTemp(next: boolean): Promise<void> {
    try {
      await setReceivingTempEnabled.mutateAsync(next);
      setToast(next
        ? 'Temperatura per articolo attivata (chiesta al Carico DDT)'
        : 'Temperatura per articolo disattivata');
    } catch (e) {
      setToast(toUserMessage(e));
    }
  }

  async function onCheckUpdate(): Promise<void> {
    setUpdateChecking(true);
    setUpdateCheck(null);
    try {
      const result = await checkForUpdate();
      setUpdateCheck(result);
      // Distingui i 3 esiti: errore di rete (manifest irraggiungibile),
      // versione uguale (davvero aggiornata), versione diversa (update disponibile).
      if (result.error) {
        setToast(`Impossibile verificare: ${result.error}`);
      } else if (!result.available) {
        setToast(`App già aggiornata (${result.current}).`);
      }
    } catch (e) {
      setToast(`Errore: ${toUserMessage(e)}`);
    } finally {
      setUpdateChecking(false);
    }
  }

  async function onApplyUpdate(): Promise<void> {
    // Conferma prima del kill processo: l'utente deve sapere che l'app si
    // chiuderà e dovrà essere riaperta a mano.
    const ok = await confirmDialog(
      "Per applicare l'aggiornamento l'app si chiuderà al termine del download. Dovrai riaprirla manualmente. Continuare?",
      { header: 'Aggiornamento app', okText: 'Continua' },
    );
    if (!ok) return;
    setUpdateProgress(0);
    try {
      await downloadAndApplyUpdate((pct) => setUpdateProgress(pct));
      // Bundle scaricato e schedulato come "next". Killiamo il processo:
      // il prossimo avvio carica il nuovo bundle in cold start pulito.
      await restartAppForUpdate();
    } catch (e) {
      setUpdateProgress(null);
      setToast(toUserMessage(e));
    }
  }

  async function onSwitchTenant(tenantId: string): Promise<void> {
    await switchTenant(tenantId);
  }
  async function onSwitchBusiness(businessId: string): Promise<void> {
    if (!businessId) return;
    await switchBusiness(businessId);
  }
  // T-073: stato per il flusso di eliminazione locale (solo platform owner).
  // Soft-delete: setta `archived_at` sul business. Le query app filtrano già
  // per `archived_at IS NULL` quindi sparisce dal switcher e da tutte le viste.
  // I dati storici (productions, events, documents) restano per audit HACCP.
  const [deleteBusinessOpen, setDeleteBusinessOpen] = useState(false);
  const [deletingBusiness, setDeletingBusiness] = useState(false);
  async function archiveActiveBusiness(): Promise<void> {
    const activeBiz = tenantBusinesses.find((b) => b.id === profile?.active_business_id);
    if (!activeBiz) {
      setToast('Nessun locale attivo da eliminare');
      return;
    }
    if (tenantBusinesses.length <= 1) {
      setToast('Non puoi eliminare l\'unico locale rimasto. Creane prima un altro.');
      return;
    }
    setDeletingBusiness(true);
    try {
      const { error } = await getSupabase()
        .from('businesses')
        .update({ archived_at: new Date().toISOString() })
        .eq('id', activeBiz.id);
      if (error) throw error;
      // Cache locale: rimuovi dalla lista remota (i masters_local si
      // ri-allineano al prossimo sync-pull, ma intanto la UI risponde subito).
      setRemoteBusinesses((prev) => prev.filter((b) => b.id !== activeBiz.id));
      // Switcha sul primo business rimasto.
      const fallback = tenantBusinesses.find((b) => b.id !== activeBiz.id);
      if (fallback) {
        await switchBusiness(fallback.id);
      }
      celebrate(`Locale "${activeBiz.name}" eliminato`);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setDeletingBusiness(false);
    }
  }

  async function createBusiness(): Promise<void> {
    const name = newBusinessName.trim();
    if (!name) {
      setToast('Inserisci il nome del locale');
      return;
    }
    if (!profile?.active_tenant_id) {
      setToast('Nessuna azienda attiva');
      return;
    }
    setCreatingBusiness(true);
    try {
      const { data, error } = await getSupabase()
        .from('businesses')
        .insert({ tenant_id: profile.active_tenant_id, name })
        .select('id')
        .single();
      if (error || !data) throw new Error(error?.message ?? 'insert_failed');
      const newId = (data as { id: string }).id;
      setRemoteBusinesses((prev) => [
        ...prev,
        { id: newId, tenant_id: profile.active_tenant_id!, name },
      ]);
      await switchBusiness(newId);
      setNewBusinessName('');
      celebrate(`Locale "${name}" creato`);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setCreatingBusiness(false);
    }
  }

  // T-062: il consulente / platform owner crea da qui un nuovo ristorante
  // demo (= nuovo tenant + business con LO STESSO nome placeholder). Serve
  // per fare setup pre-vendita: il consulente popola dati, mostra l'app al
  // potenziale cliente; il cliente quando si registra (anche via invito
  // email — vedi sendCustomerInvite sotto) eredita questo tenant senza
  // dover fare il bootstrap da zero.
  async function createDemoTenant(): Promise<void> {
    const name = demoTenantName.trim();
    if (!name) { setToast('Inserisci il nome del ristorante / azienda'); return; }
    setCreatingDemoTenant(true);
    try {
      const { data, error } = await getSupabase().rpc('create_demo_tenant', {
        p_tenant_name: name,
        p_business_name: name,
      });
      if (error) throw new Error(error.message);
      const result = (data ?? {}) as { tenant_id?: string; business_id?: string };
      if (!result.tenant_id) throw new Error('Risposta server non valida');
      // Switch immediato sul nuovo tenant: refresh JWT include i nuovi tenants[].
      await switchTenant(result.tenant_id);
      if (result.business_id) {
        try { await switchBusiness(result.business_id); } catch { /* tenant ok, business retry al prossimo render */ }
      }
      // Pre-seleziona questo tenant nel form "Invita ristoratore" subito sotto,
      // così il flusso "crea demo → invita cliente" è un singolo gesto continuo.
      setLastCreatedDemoTenantId(result.tenant_id);
      setInviteTargetTenant(result.tenant_id);
      setDemoTenantName('');
      celebrate(`Ristorante demo "${name}" creato`);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setCreatingDemoTenant(false);
    }
  }

  // T-063: il consulente invita il ristoratore (cliente reale) sull'ambiente
  // demo pre-popolato. Riusa l'edge function `invite-staff` con
  // app_role='ristoratore' — l'invite-staff genera token + URL, il cliente
  // apre il link e su signup l'`accept-invite` lo aggiunge come membro del
  // tenant (saltando la creazione di un tenant nuovo). Per fare l'invito su
  // un tenant diverso da quello attivo, switchiamo prima il contesto e poi
  // ripristiniamo: `invite-staff` legge il tenant dal claim JWT.
  async function sendCustomerInvite(): Promise<void> {
    const email = inviteEmail.trim().toLowerCase();
    const name = inviteCustomerName.trim();
    const targetTenant = inviteTargetTenant.trim();
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(email)) { setToast('Email non valida'); return; }
    if (!name) { setToast('Indica il nome del titolare'); return; }
    if (!targetTenant) { setToast('Seleziona il ristorante a cui invitare'); return; }
    setSendingInvite(true);
    setInviteUrl(null);
    setInviteEmailSent(false);
    const previousTenant = profile?.active_tenant_id ?? null;
    try {
      // invite-staff usa il tenant attivo dal claim JWT: se il consulente
      // sta puntando un altro tenant, swap temporaneo prima della chiamata.
      if (previousTenant !== targetTenant) {
        await switchTenant(targetTenant);
      }
      const { data, error } = await getSupabase().functions.invoke('invite-staff', {
        body: {
          email,
          display_name: name,
          kitchen_role: 'Titolare',
          app_role: 'ristoratore',
        },
      });
      if (error) throw error;
      const payload = (data ?? {}) as { invite_url?: string; email_sent?: boolean; email_error?: string };
      if (!payload.invite_url) throw new Error('invite_url mancante nella risposta');
      setInviteUrl(payload.invite_url);
      setInviteEmailSent(payload.email_sent === true);
      if (payload.email_sent) {
        setToast(`Email inviata a ${email}`);
      } else if (payload.email_error) {
        setToast(`Invito creato ma email non inviata (${payload.email_error}) — condividi il link manualmente`);
      }
    } catch (err) {
      setToast(`Errore invito: ${toUserMessage(err, 'sconosciuto')}`);
    } finally {
      // Best-effort restore: se l'invio è partito da un tenant diverso da
      // quello attivo iniziale, torna a quello iniziale per non confondere
      // il consulente con un context switch silenzioso.
      try {
        if (previousTenant && previousTenant !== targetTenant) {
          await switchTenant(previousTenant);
        }
      } catch { /* non bloccare la UI per un mancato re-switch */ }
      setSendingInvite(false);
    }
  }

  async function copyInviteUrl(): Promise<void> {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setToast('Link copiato — incollalo in WhatsApp/email al cliente');
    } catch {
      setToast('Copia fallita — seleziona manualmente il link');
    }
  }

  async function shareInvite(): Promise<void> {
    if (!inviteUrl) return;
    const tName = tenantNameById.get(inviteTargetTenant) ?? 'il tuo ristorante';
    const text = `Ciao ${inviteCustomerName.trim()}, ti ho preparato l'ambiente TrackEAT per ${tName}. Apri questo link per attivare il tuo account (usa l'email a cui ti ho mandato il link):\n\n${inviteUrl}`;
    if (typeof navigator.share === 'function') {
      try { await shareWithNavigator({ title: 'Invito TrackEAT', text }); return; } catch { /* fallthrough */ }
    }
    try {
      await navigator.clipboard.writeText(text);
      setToast('Messaggio copiato — incollalo in WhatsApp/email');
    } catch { setToast('Copia fallita'); }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t('settings.title')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        {(() => {
          const displayName = profile?.full_name ?? user?.email?.split('@')[0] ?? '—';
          const initials = displayName
            .trim()
            .split(/\s+/)
            .slice(0, 2)
            .map((w) => w[0]?.toUpperCase() ?? '')
            .join('') || '?';
          const activeBusiness = tenantBusinesses.find((b) => b.id === profile?.active_business_id);
          const syncStatusColor: Record<string, string> = {
            idle: '#15803d',
            ok: '#15803d',
            pulling: '#0066cc',
            pushing: '#0066cc',
            error: '#dc2626',
            offline: '#94a3b8',
          };
          const attentionCount = sync.queueTenantId === profile?.active_tenant_id
            ? sync.queueAttentionCount ?? 0 : 0;
          const syncDotColor = attentionCount > 0 ? '#b45309' : syncStatusColor[sync.status] ?? '#94a3b8';
          const activeModuleCount = [
            warehouseEnabled,
            collettivaEnabled,
            transportDocumentsEnabled,
            productionLotPhotoEnabled,
            foodCostEnabled,
            eventQuotesEnabled,
            aiAgentEnabled,
            recipeQtyEnabled,
            receivingTempEnabled,
          ].filter(Boolean).length;
          return (
            <div style={{ padding: '12px 16px 4px' }}>
              {/* ── PROFILO HERO ── */}
              <div style={{
                background: '#fff',
                borderRadius: 16,
                padding: '16px',
                boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05), 0 4px 12px rgba(15,23,42,.04)',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                marginBottom: 12,
              }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 16,
                  background: 'linear-gradient(135deg,#0066cc,#8b5cf6)',
                  color: '#fff', fontSize: 22, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {initials}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {displayName}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>
                    {user?.email}
                  </div>
                  {profile?.role && (
                    <span style={{
                      display: 'inline-block', marginTop: 6,
                      background: '#eff6ff', color: '#0066cc',
                      fontSize: 10, fontWeight: 700,
                      padding: '3px 10px', borderRadius: 999,
                      textTransform: 'uppercase', letterSpacing: '0.04em',
                    }}>
                      {profile.role}
                    </span>
                  )}
                </div>
              </div>

              {isConsultantOrOwner && (
                <section
                  aria-label="Pannello impostazioni consulente"
                  style={{
                    background: 'linear-gradient(145deg,#0f172a,#1e3a5f)',
                    borderRadius: 18,
                    padding: '16px',
                    boxShadow: '0 8px 24px rgba(15,23,42,.14)',
                    marginBottom: 12,
                  }}
                >
                  <div style={{ color: '#fff', fontSize: 17, fontWeight: 800 }}>
                    Pannello consulente
                  </div>
                  <div style={{ color: '#cbd5e1', fontSize: 12, marginTop: 4, lineHeight: 1.4 }}>
                    {activeBusiness?.name ?? 'Nessun locale selezionato'} · {activeModuleCount} {activeModuleCount === 1 ? 'funzione attiva' : 'funzioni attive'}
                  </div>
                  <IonSegment
                    value={consultantSection}
                    onIonChange={(event) => {
                      const next = event.detail.value;
                      if (next === 'modules' || next === 'client' || next === 'app') {
                        setConsultantSection(next);
                      }
                    }}
                    scrollable
                    style={{
                      marginTop: 14,
                      background: 'rgba(255,255,255,.1)',
                      borderRadius: 12,
                      padding: 3,
                    }}
                  >
                    <IonSegmentButton value="modules" style={{ minHeight: 46, '--color': '#cbd5e1', '--color-checked': '#ffffff', '--indicator-color': '#ffffff' } as React.CSSProperties}>
                      <IonIcon icon={optionsOutline} aria-hidden="true" />
                      <span style={{ fontSize: 11, fontWeight: 700 }}>Moduli</span>
                    </IonSegmentButton>
                    <IonSegmentButton value="client" style={{ minHeight: 46, '--color': '#cbd5e1', '--color-checked': '#ffffff', '--indicator-color': '#ffffff' } as React.CSSProperties}>
                      <IonIcon icon={businessOutline} aria-hidden="true" />
                      <span style={{ fontSize: 11, fontWeight: 700 }}>Cliente</span>
                    </IonSegmentButton>
                    <IonSegmentButton value="app" style={{ minHeight: 46, '--color': '#cbd5e1', '--color-checked': '#ffffff', '--indicator-color': '#ffffff' } as React.CSSProperties}>
                      <IonIcon icon={appsOutline} aria-hidden="true" />
                      <span style={{ fontSize: 11, fontWeight: 700 }}>App e strumenti</span>
                    </IonSegmentButton>
                  </IonSegment>
                </section>
              )}

              {/* ── AGGIORNAMENTI APP — T-017: portato in cima, restylato per
                  versione/data leggibili. Bordo verde quando c'è un update. ── */}
              {(!isConsultantOrOwner || consultantSection === 'app') && (() => {
                const hasUpdate = updateCheck?.available === true;
                const hasError = !!updateCheck?.error;
                const isUpToDate =
                  updateCheck && !updateCheck.available && !updateCheck.error && !!updateCheck.latest;
                // Parsing della versione installata. T-018: formato nuovo è
                //   MAJOR.MINOR.PATCH.BUILD-YYYYMMDD-HHMMSS
                //     (con un eventuale "-beta-" prima del timestamp sul canale beta)
                // Per retro-compatibilità accettiamo anche il vecchio formato
                //   MAJOR.MINOR.PATCH-YYYYMMDD-HHMMSS (senza build counter)
                // Fonti in cascata: il check appena fatto > la versione caricata
                // al mount > "—" se proprio non sappiamo nulla.
                const raw = updateCheck?.current ?? installedVersion ?? '';
                const months = ['gennaio','febbraio','marzo','aprile','maggio','giugno','luglio','agosto','settembre','ottobre','novembre','dicembre'];
                let semver = raw || '—';
                let buildNumber: number | null = null;
                let isBeta = false;
                let installedDate: string | null = null;
                // 4-part (con build counter), eventuale -beta- prima del timestamp
                const mNew = /^(\d+\.\d+\.\d+)\.(\d+)(?:-beta)?-(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})$/.exec(raw);
                if (mNew) {
                  const [, sv, b, y, mo, d, hh, mm] = mNew;
                  semver = sv;
                  buildNumber = parseInt(b, 10);
                  isBeta = raw.includes('-beta-');
                  installedDate = `${parseInt(d, 10)} ${months[parseInt(mo, 10) - 1]} ${y} alle ${hh}:${mm}`;
                } else {
                  // 3-part legacy (senza build counter), eventuale -beta-
                  const mOld = /^(\d+\.\d+\.\d+)(?:-beta)?-(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})$/.exec(raw);
                  if (mOld) {
                    const [, sv, y, mo, d, hh, mm] = mOld;
                    semver = sv;
                    isBeta = raw.includes('-beta-');
                    installedDate = `${parseInt(d, 10)} ${months[parseInt(mo, 10) - 1]} ${y} alle ${hh}:${mm}`;
                  }
                }
                const accentBorder = hasUpdate ? '#16a34a' : hasError ? '#dc2626' : 'transparent';
                return (
                  <div style={{
                    background: '#fff',
                    borderRadius: 16,
                    padding: '18px 18px',
                    boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05), 0 4px 12px rgba(15,23,42,.04)',
                    marginBottom: 12,
                    border: `2px solid ${accentBorder}`,
                  }}>
                    <div style={{
                      fontSize: 11, fontWeight: 700, color: '#94a3b8',
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                    }}>
                      📲 {isNative ? 'Versione installata' : 'Versione in uso'}
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
                      marginTop: 4,
                    }}>
                      <span style={{
                        fontSize: 24, fontWeight: 800, color: '#0f172a',
                        fontVariantNumeric: 'tabular-nums',
                        letterSpacing: '-0.02em',
                      }}>
                        v{semver}
                      </span>
                      {buildNumber !== null && (
                        <span style={{
                          fontSize: 13, fontWeight: 700, color: '#64748b',
                          background: '#f1f5f9', padding: '3px 10px',
                          borderRadius: 999, fontVariantNumeric: 'tabular-nums',
                        }}>
                          build #{buildNumber}
                        </span>
                      )}
                      {isBeta && (
                        <span style={{
                          fontSize: 11, fontWeight: 700, color: '#a16207',
                          background: '#fef3c7', padding: '3px 10px',
                          borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.04em',
                        }}>
                          beta
                        </span>
                      )}
                    </div>
                    {installedDate && (
                      <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                        {isNative ? 'Aggiornata il' : 'Versione del'} {installedDate}
                      </div>
                    )}

                    {hasUpdate && updateCheck?.latest && (
                      <div style={{
                        marginTop: 12, padding: '10px 12px',
                        background: '#dcfce7', borderRadius: 10,
                        fontSize: 13, color: '#166534',
                      }}>
                        <div style={{ fontWeight: 700 }}>
                          Aggiornamento disponibile
                        </div>
                        <div style={{ marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                          Nuova versione: <b>{updateCheck.latest}</b>
                        </div>
                        {updateCheck.notes && (
                          <div style={{ marginTop: 6, fontSize: 12, color: '#15803d', fontStyle: 'italic' }}>
                            {updateCheck.notes}
                          </div>
                        )}
                      </div>
                    )}
                    {isUpToDate && !hasUpdate && (
                      <div style={{
                        display: 'inline-block', marginTop: 10,
                        background: '#dcfce7', color: '#15803d',
                        fontSize: 11, fontWeight: 700,
                        padding: '4px 12px', borderRadius: 999,
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                      }}>
                        ✓ App aggiornata
                      </div>
                    )}
                    {hasError && updateCheck?.error && (
                      <div style={{
                        marginTop: 10, padding: '8px 10px',
                        background: '#fee2e2', borderRadius: 10,
                        fontSize: 12, color: '#b91c1c',
                      }}>
                        <b>Errore verifica:</b> {updateCheck.error}
                      </div>
                    )}

                    {isNative ? <div style={{ marginTop: 14 }}>
                      {hasUpdate ? (
                        <IonButton
                          expand="block"
                          color="success"
                          disabled={updateProgress !== null}
                          onClick={() => { void onApplyUpdate(); }}
                          style={{
                            '--border-radius': '12px',
                            '--padding-top': '14px',
                            '--padding-bottom': '14px',
                            fontWeight: 700,
                          } as React.CSSProperties}
                        >
                          {updateProgress !== null ? <IonSpinner name="dots" /> : '⬇ Installa aggiornamento'}
                        </IonButton>
                      ) : (
                        <IonButton
                          expand="block"
                          fill="outline"
                          disabled={updateChecking}
                          onClick={() => { void onCheckUpdate(); }}
                          style={{
                            '--border-radius': '12px',
                            '--padding-top': '12px',
                            '--padding-bottom': '12px',
                          } as React.CSSProperties}
                        >
                          {updateChecking ? <IonSpinner name="dots" /> : 'Verifica aggiornamenti'}
                        </IonButton>
                      )}
                    </div> : (
                      <p style={{ marginTop: 14, marginBottom: 0, fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
                        Gli aggiornamenti vengono scaricati quando sei connesso a Internet.
                        Per applicarli, salva il lavoro, chiudi tutte le schede e finestre di TrackEAT,
                        compresa l’app aperta dalla schermata Home, poi riaprila.
                        Offline puoi continuare a usare la versione già disponibile.
                      </p>
                    )}
                    {updateProgress !== null && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{
                          display: 'flex', justifyContent: 'space-between',
                          fontSize: 12, color: '#64748b', marginBottom: 6,
                        }}>
                          <span>Download in corso…</span>
                          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                            {updateProgress}%
                          </span>
                        </div>
                        <IonProgressBar value={updateProgress / 100} />
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── LOCALE ATTIVO ── */}
              <div style={{
                display: !isConsultantOrOwner || consultantSection === 'client' ? 'block' : 'none',
                background: '#fff',
                borderRadius: 16,
                padding: '14px 16px',
                boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                marginBottom: 12,
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, color: '#94a3b8',
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  marginBottom: 6,
                }}>
                  Locale attivo
                </div>
                {tenantBusinesses.length > 0 ? (
                  <IonSelect
                    interface="action-sheet"
                    value={profile?.active_business_id ?? ''}
                    onIonChange={(ev) => { void onSwitchBusiness(String(ev.detail.value ?? '')); }}
                    style={{
                      '--padding-start': 0,
                      '--padding-end': 0,
                      fontSize: 18, fontWeight: 700, color: '#0f172a',
                    } as React.CSSProperties}
                  >
                    {tenantBusinesses.map((b) => (
                      <IonSelectOption key={b.id} value={b.id}>
                        {b.name}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                ) : (
                  <div style={{ fontSize: 14, color: '#94a3b8', fontStyle: 'italic' }}>
                    {t('settings.noBusiness')}
                  </div>
                )}
                {(profile?.tenants?.length ?? 0) > 1 && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                      Azienda
                    </div>
                    <IonSelect
                      interface="action-sheet"
                      value={profile?.active_tenant_id ?? ''}
                      onIonChange={(ev) => onSwitchTenant(String(ev.detail.value ?? ''))}
                      style={{ '--padding-start': 0, '--padding-end': 0, fontSize: 13, color: '#475569' } as React.CSSProperties}
                    >
                      {(profile?.tenants ?? []).map((tid) => (
                        <IonSelectOption key={tid} value={tid}>
                          {tenantNameById.get(tid) ?? `Ristorante ${tid.slice(0, 8)}`}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </div>
                )}
                {/* T-014/T-244: la creazione di un nuovo locale è un'azione di
                    control-plane del consulente — visibile al platform owner E a
                    ogni consulente (sui propri tenant). I ristoratori/osa/staff
                    usano solo lo switcher sopra. */}
                {isConsultantOrOwner && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e2e8f0', display: 'flex', gap: 8, alignItems: 'center' }}>
                    <IonInput
                      placeholder="Nuovo locale…"
                      value={newBusinessName}
                      onIonInput={(ev) => setNewBusinessName(String(ev.detail.value ?? ''))}
                      style={{
                        flex: 1, fontSize: 13,
                        '--padding-start': '10px', '--padding-end': '10px',
                        '--padding-top': '8px', '--padding-bottom': '8px',
                        '--background': '#f1f5f9',
                        '--border-radius': '10px',
                        '--color': '#0f172a',
                      } as React.CSSProperties}
                    />
                    <IonButton
                      size="small"
                      fill="solid"
                      disabled={creatingBusiness || !newBusinessName.trim()}
                      onClick={() => { void createBusiness(); }}
                      style={{ '--border-radius': '10px', flexShrink: 0 } as React.CSSProperties}
                    >
                      {creatingBusiness ? <IonSpinner name="dots" /> : 'Crea'}
                    </IonButton>
                  </div>
                )}
                {/* T-073: bottone "Elimina locale attivo" — owner-only,
                    disabilitato se è l'unico locale del tenant (non si
                    azzera tutto). Soft-delete via archived_at: i dati
                    storici (productions, eventi HACCP) restano disponibili
                    per audit anche dopo che il locale sparisce dal switcher. */}
                {isConsultantOrOwner && tenantBusinesses.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <IonButton
                      size="small"
                      fill="outline"
                      color="danger"
                      disabled={deletingBusiness || tenantBusinesses.length <= 1}
                      onClick={() => setDeleteBusinessOpen(true)}
                      style={{ '--border-radius': '10px', fontSize: 12 } as React.CSSProperties}
                    >
                      {deletingBusiness ? <IonSpinner name="dots" /> : '🗑️ Elimina locale attivo'}
                    </IonButton>
                    {tenantBusinesses.length <= 1 && (
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, fontStyle: 'italic' }}>
                        Per eliminare l'unico locale crea prima un altro locale.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── CREA RISTORANTE DEMO — solo platform owner / consulente ──
                  T-062: ambiente di prova per il ristoratore potenziale.
                  Un solo input perché il "nome ristorante" è un placeholder
                  che vale come ragione sociale (tenant) E come nome del primo
                  locale (business): è un workspace vergine, il cliente vero
                  lo personalizzerà una volta accettato l'invito (T-063).
                  T-244: ora visibile a ogni consulente, non solo all'owner. */}
              {isConsultantOrOwner && consultantSection === 'client' && (
                <div style={{
                  background: '#fff',
                  borderRadius: 16,
                  padding: '14px 16px',
                  boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                  marginBottom: 12,
                  border: '1px dashed #cbd5e1',
                }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
                  }}>
                    <div style={{
                      fontSize: 11, fontWeight: 700, color: '#94a3b8',
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                    }}>
                      Nuovo ristorante demo
                    </div>
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: '#7c3aed',
                      background: '#ede9fe', padding: '2px 8px',
                      borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.04em',
                    }}>
                      Consulente
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10, lineHeight: 1.4 }}>
                    Crea un ambiente vergine — basta un nome placeholder, sarà
                    sia il "ristorante" sia la "azienda". Quando inviti il
                    cliente qui sotto, lui erediterà questo ambiente.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <IonInput
                      placeholder="Nome ristorante / azienda (es. Trattoria Da Mario)"
                      value={demoTenantName}
                      onIonInput={(ev) => setDemoTenantName(String(ev.detail.value ?? ''))}
                      style={{
                        fontSize: 14,
                        '--padding-start': '10px', '--padding-end': '10px',
                        '--padding-top': '10px', '--padding-bottom': '10px',
                        '--background': '#f1f5f9',
                        '--border-radius': '10px',
                        '--color': '#0f172a',
                      } as React.CSSProperties}
                    />
                    <IonButton
                      expand="block"
                      fill="solid"
                      color="primary"
                      disabled={creatingDemoTenant || !demoTenantName.trim()}
                      onClick={() => { void createDemoTenant(); }}
                      style={{
                        '--border-radius': '12px',
                        '--padding-top': '12px',
                        '--padding-bottom': '12px',
                        fontWeight: 700,
                        marginTop: 2,
                      } as React.CSSProperties}
                    >
                      {creatingDemoTenant ? <IonSpinner name="dots" /> : '+ Crea ristorante demo'}
                    </IonButton>
                  </div>
                </div>
              )}

              {/* ── INVITA RISTORATORE — solo platform owner / consulente ──
                  T-063: il consulente manda al ristoratore (cliente reale)
                  un link di invito. Quando il cliente apre il link e si
                  registra, l'edge function `accept-invite` lo aggiunge come
                  membro del tenant pre-popolato — niente bootstrap, niente
                  ricreazione del ristorante. Riusa l'infra esistente
                  (invite-staff + accept-invite + AcceptInvitePage). */}
              {isConsultantOrOwner && consultantSection === 'client' && (profile?.tenants?.length ?? 0) > 0 && (
                <div style={{
                  background: '#fff',
                  borderRadius: 16,
                  padding: '14px 16px',
                  boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                  marginBottom: 12,
                  border: '1px dashed #c7d2fe',
                }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
                  }}>
                    <div style={{
                      fontSize: 11, fontWeight: 700, color: '#94a3b8',
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                    }}>
                      Invita ristoratore
                    </div>
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: '#1d4ed8',
                      background: '#dbeafe', padding: '2px 8px',
                      borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.04em',
                    }}>
                      Consulente
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10, lineHeight: 1.4 }}>
                    Manda al cliente un link di invito. Quando si registra con
                    questa email, salta la creazione del ristorante ed eredita
                    direttamente l'ambiente che hai preparato.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <IonSelect
                      interface="action-sheet"
                      label="Ristorante destinazione"
                      labelPlacement="stacked"
                      placeholder="Scegli il ristorante"
                      value={inviteTargetTenant}
                      onIonChange={(ev) => setInviteTargetTenant(String(ev.detail.value ?? ''))}
                      style={{
                        fontSize: 14,
                        '--background': '#f1f5f9',
                        '--border-radius': '10px',
                        '--padding-start': '10px', '--padding-end': '10px',
                        '--padding-top': '8px', '--padding-bottom': '8px',
                      } as React.CSSProperties}
                    >
                      {(profile?.tenants ?? []).map((tid) => (
                        <IonSelectOption key={tid} value={tid}>
                          {tenantNameById.get(tid) ?? `Ristorante ${tid.slice(0, 8)}`}
                          {tid === lastCreatedDemoTenantId ? ' · appena creato' : ''}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                    <IonInput
                      placeholder="Nome del titolare (es. Mario Rossi)"
                      value={inviteCustomerName}
                      onIonInput={(ev) => setInviteCustomerName(String(ev.detail.value ?? ''))}
                      style={{
                        fontSize: 14,
                        '--padding-start': '10px', '--padding-end': '10px',
                        '--padding-top': '10px', '--padding-bottom': '10px',
                        '--background': '#f1f5f9',
                        '--border-radius': '10px',
                        '--color': '#0f172a',
                      } as React.CSSProperties}
                    />
                    <IonInput
                      type="email"
                      inputmode="email"
                      autocapitalize="off"
                      placeholder="Email del cliente"
                      value={inviteEmail}
                      onIonInput={(ev) => setInviteEmail(String(ev.detail.value ?? ''))}
                      style={{
                        fontSize: 14,
                        '--padding-start': '10px', '--padding-end': '10px',
                        '--padding-top': '10px', '--padding-bottom': '10px',
                        '--background': '#f1f5f9',
                        '--border-radius': '10px',
                        '--color': '#0f172a',
                      } as React.CSSProperties}
                    />
                    <IonButton
                      expand="block"
                      fill="solid"
                      color="primary"
                      disabled={
                        sendingInvite
                        || !inviteEmail.trim()
                        || !inviteCustomerName.trim()
                        || !inviteTargetTenant
                      }
                      onClick={() => { void sendCustomerInvite(); }}
                      style={{
                        '--border-radius': '12px',
                        '--padding-top': '12px',
                        '--padding-bottom': '12px',
                        fontWeight: 700,
                        marginTop: 2,
                      } as React.CSSProperties}
                    >
                      {sendingInvite ? <IonSpinner name="dots" /> : 'Genera invito'}
                    </IonButton>
                    {inviteUrl && (
                      <div style={{
                        marginTop: 6, padding: '10px 12px',
                        background: '#ecfdf5', borderRadius: 10,
                        border: '1px solid #a7f3d0',
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                          {inviteEmailSent
                            ? `✉️ Email inviata · valido 14 giorni`
                            : 'Invito pronto · valido 14 giorni'}
                        </div>
                        <div style={{ fontSize: 12, color: '#0f172a', marginBottom: 6, lineHeight: 1.4 }}>
                          {inviteEmailSent
                            ? `Il cliente riceverà l'invito su ${inviteEmail || 'la sua email'}. Se non lo trova nei prossimi minuti, controlla anche lo spam o condividi il link manualmente:`
                            : 'L\'email automatica non è partita: condividi il link manualmente al cliente.'}
                        </div>
                        <div style={{ fontSize: 12, color: '#0f172a', wordBreak: 'break-all', fontFamily: 'ui-monospace,monospace', background: '#fff', padding: '6px 8px', borderRadius: 6, marginBottom: 8 }}>
                          {inviteUrl}
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <IonButton size="small" fill="outline" onClick={() => { void copyInviteUrl(); }} style={{ '--border-radius': '8px', flex: 1 } as React.CSSProperties}>
                            Copia link
                          </IonButton>
                          <IonButton size="small" fill="solid" color="success" onClick={() => { void shareInvite(); }} style={{ '--border-radius': '8px', flex: 1 } as React.CSSProperties}>
                            Condividi
                          </IonButton>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── CONDIVIDI L'APP — solo consulente/owner (T-267) ── */}
              {isConsultantOrOwner && consultantSection === 'client' && <ShareAppCard onToast={setToast} />}

              {isConsultantOrOwner && consultantSection === 'app' && (
                <div style={{
                  background: '#fff', borderRadius: 14, padding: '14px 16px',
                  boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                  marginBottom: 12,
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Lingua dell’app
                  </div>
                  <IonSelect
                    aria-label="Lingua dell’app"
                    interface="action-sheet"
                    value={i18n.language.startsWith('en') ? 'en' : 'it'}
                    onIonChange={(ev) => {
                      const v = String(ev.detail.value ?? 'it');
                      setLanguage(v === 'en' ? 'en' : 'it');
                    }}
                    style={{ '--padding-start': 0, '--padding-end': 0, minHeight: 44, fontSize: 15, fontWeight: 600, color: '#0f172a' } as React.CSSProperties}
                  >
                    <IonSelectOption value="it">Italiano</IonSelectOption>
                    <IonSelectOption value="en">English</IonSelectOption>
                  </IonSelect>
                </div>
              )}

              {/* ── MODULI ATTIVABILI — consulente/owner ──
                  T-011: la card Magazzino (toggle Pro/Base) era owner-only.
                  T-244: ora visibile anche ai consulenti (attivano Magazzino Pro
                  e Collettiva sui propri tenant; backend gated su owner/consulente). */}
              {isConsultantOrOwner && consultantSection === 'modules' && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr)',
                  gap: 8,
                  marginBottom: 12,
                }}>
                  <div style={{
                    padding: '2px 2px 6px',
                  }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
                      Moduli del cliente
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 3, lineHeight: 1.45 }}>
                      Attiva solo ciò che serve a {activeBusiness?.name ?? 'questo locale'}. Le dipendenze vengono gestite automaticamente.
                    </div>
                  </div>

                  {/* Piano / Magazzino */}
                  <div style={{
                    background: '#fff',
                    borderRadius: 14,
                    padding: '12px 14px',
                    boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                  }}>
                    <div style={{
                      fontSize: 11, fontWeight: 600, color: '#94a3b8',
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                    }}>
                      Magazzino
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, lineHeight: 1.35 }}>
                      Giacenze, carichi, scarichi e lotti. Richiesto dalla Collettiva.
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: warehouseEnabled ? '#15803d' : '#94a3b8' }}>
                        {warehouseEnabled ? 'Pro' : 'Base'}
                      </span>
                      <IonToggle
                        checked={warehouseEnabled}
                        disabled={togglingWarehouse}
                        onIonChange={(ev) => { void onToggleWarehouse(!!ev.detail.checked); }}
                      />
                    </div>

                    <div style={{
                      marginTop: 12,
                      borderTop: '1px solid #e2e8f0',
                      paddingTop: 10,
                    }}>
                      <div style={{
                        fontSize: 10, fontWeight: 700, color: '#64748b',
                        textTransform: 'uppercase', letterSpacing: '0.06em',
                        marginBottom: 2,
                      }}>
                        Funzioni collegate
                      </div>

                      {/* T-130: con Magazzino Pro le quantità sono obbligatorie;
                          in modalità Base restano configurabili. */}
                      <div id="recipe-qty" style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 0', minHeight: 56,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                            Quantità ricette
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, lineHeight: 1.35 }}>
                            {warehouseEnabled
                              ? 'Obbligatorie con Magazzino Pro'
                              : 'Dosi degli ingredienti nelle ricette'}
                          </div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: recipeQtyEnabled ? '#15803d' : '#94a3b8', marginTop: 3 }}>
                            {recipeQtyEnabled ? 'Attive' : 'Disattive'}
                          </div>
                        </div>
                        <IonToggle
                          aria-label="Quantità ricette"
                          checked={recipeQtyEnabled}
                          disabled={togglingRecipeQty || warehouseEnabled}
                          onIonChange={(ev) => {
                            const next = !!ev.detail.checked;
                            if (next === recipeQtyEnabled) return;
                            setRecipeQtyConfirm(next);
                          }}
                        />
                      </div>

                      {/* T-563: collegato alla logistica di magazzino, ma resta
                          utilizzabile anche in modalità Base senza giacenze. */}
                      <div style={{
                        borderTop: '1px solid #e2e8f0',
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 0 2px', minHeight: 56,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                            DDT ristorante
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, lineHeight: 1.35 }}>
                            Vendite e trasferimenti dai lotti prodotti, disponibile anche in Base
                          </div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: transportDocumentsEnabled ? '#15803d' : '#94a3b8', marginTop: 3 }}>
                            {transportDocumentsEnabled ? 'Attivo' : 'Off'}
                          </div>
                        </div>
                        <IonToggle
                          aria-label="DDT ristorante"
                          checked={transportDocumentsEnabled}
                          disabled={togglingTransportDocuments}
                          onIonChange={(ev) => { void onToggleTransportDocuments(!!ev.detail.checked); }}
                        />
                      </div>

                      {/* T-628: modulo indipendente, governato dall'RPC tenant-scoped. */}
                      {canManageFeatures && (
                        <div style={{
                          borderTop: '1px solid #e2e8f0',
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '10px 0 2px', minHeight: 56,
                        }}>
                          <IonIcon
                            icon={cameraOutline}
                            aria-hidden="true"
                            style={{ fontSize: 20, color: '#475569', flex: '0 0 auto' }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                              Foto lotti in produzione
                            </div>
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, lineHeight: 1.35 }}>
                              Fotografa i lotti; lettura AI dopo la stampa, anche con Magazzino Base
                            </div>
                            <div style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: productionLotPhotoEnabled ? '#15803d' : '#64748b',
                              marginTop: 3,
                            }}>
                              {productionLotPhotoEnabled ? 'Attiva' : 'Disattivata'}
                            </div>
                          </div>
                          <IonToggle
                            aria-label="Acquisizione foto dei lotti in produzione"
                            checked={productionLotPhotoEnabled}
                            disabled={togglingProductionLotPhoto}
                            onIonChange={(ev) => {
                              void onToggleProductionLotPhoto(!!ev.detail.checked);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* T-215: modulo ristorazione collettiva (owner-only come il
                      toggle Magazzino: si attiva per i tenant industria, es.
                      Bluecoop). Abilita scheda produzione giornaliera, DDT in
                      uscita, trasferimenti interni, anagrafica destinazioni. */}
                  <div style={{
                    background: '#fff',
                    borderRadius: 14,
                    padding: '12px 14px',
                    boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                  }}>
                    <div style={{
                      fontSize: 11, fontWeight: 600, color: '#94a3b8',
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                    }}>
                      Ristorazione collettiva
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, lineHeight: 1.35 }}>
                      Produzione giornaliera, destinazioni e trasferimenti strutturati.
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: collettivaEnabled ? '#15803d' : '#94a3b8' }}>
                        {collettivaEnabled ? 'Attivo' : 'Off'}
                      </span>
                      <IonToggle
                        checked={collettivaEnabled}
                        disabled={togglingCollettiva}
                        onIonChange={(ev) => { void onToggleCollettiva(!!ev.detail.checked); }}
                      />
                    </div>
                  </div>
                  {/* T-473: famiglia commerciale con due sottomoduli indipendenti.
                      Il ristorante non vede gli eventi; la sala può acquistare il
                      preventivatore anche senza il cockpit Food Cost ristorante. */}
                  <div style={{
                    gridColumn: '1 / -1', background: '#fff', borderRadius: 14,
                    padding: '14px', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Area commerciale · tipologia cliente
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 3, lineHeight: 1.35 }}>
                      Attiva soltanto i percorsi richiesti dal cliente. I due moduli sono autonomi.
                    </div>
                    <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 10, paddingTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Ristorante</div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>Ricette, costi, margine materie e decisioni menu</div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: foodCostEnabled ? '#15803d' : '#94a3b8', marginTop: 3 }}>{foodCostEnabled ? 'Attivo' : 'Off'}</div>
                      </div>
                      <IonToggle checked={foodCostEnabled} disabled={togglingFoodCost}
                        onIonChange={(ev) => { void onToggleFoodCost(!!ev.detail.checked); }} />
                    </div>
                    <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 10, paddingTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Sale Trattenimenti / Catering</div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>Eventi, preventivi, produzione, spedizioni e approvvigionamenti</div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: eventQuotesEnabled ? '#15803d' : '#94a3b8', marginTop: 3 }}>{eventQuotesEnabled ? 'Attivo' : 'Off'}</div>
                      </div>
                      <IonToggle checked={eventQuotesEnabled} disabled={togglingEventQuotes}
                        onIonChange={(ev) => { void onToggleEventQuotes(!!ev.detail.checked); }} />
                    </div>
                  </div>
                  {/* T-373: modulo Agente AI (PLACEHOLDER). L'interruttore attiva/
                      disattiva il flag; l'assistente conversazionale sui dati del
                      locale è in sviluppo (T-374, dopo la validazione del Food Cost). */}
                  <div style={{
                    background: '#fff',
                    borderRadius: 14,
                    padding: '12px 14px',
                    boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                  }}>
                    <div style={{
                      fontSize: 11, fontWeight: 600, color: '#94a3b8',
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                    }}>
                      Agente AI
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: aiAgentEnabled ? '#7c3aed' : '#94a3b8' }}>
                        {aiAgentEnabled ? 'Attivo' : 'Off'}
                      </span>
                      <IonToggle
                        checked={aiAgentEnabled}
                        disabled={togglingAiAgent}
                        onIonChange={(ev) => { void onToggleAiAgent(!!ev.detail.checked); }}
                      />
                    </div>
                    <div style={{ fontSize: 10.5, color: '#c084fc', marginTop: 4, fontWeight: 600 }}>
                      In sviluppo
                    </div>
                  </div>
                </div>
              )}

              {/* ── T-450 F1: IVA DI DEFAULT (%) — consulente/owner, solo se Food
                  Cost attivo. È l'aliquota con cui il Food Cost scorpora l'IVA dal
                  prezzo di vendita (lordo a menù); una ricetta può fare override. */}
              {isConsultantOrOwner && consultantSection === 'modules' && foodCostEnabled && (
              <div id="default-vat" style={{
                background: '#fff', borderRadius: 14, padding: '12px 14px',
                boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                marginBottom: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      💶 IVA di default
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginTop: 2 }}>
                      Aliquota per lo scorporo del food cost
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: '0 0 auto' }}>
                    <IonInput
                      type="number"
                      inputmode="decimal"
                      style={{ maxWidth: 72, textAlign: 'right', border: '1px solid #cbd5e1', borderRadius: 8 } as React.CSSProperties}
                      value={vatDraft ?? String(defaultVatRate)}
                      onIonInput={(ev) => setVatDraft(String(ev.detail.value ?? ''))}
                      onIonBlur={() => { void saveVatRate(); }}
                    />
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#334155' }}>%</span>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, lineHeight: 1.4 }}>
                  I prezzi di vendita a menù sono IVA inclusa: il food cost la scorpora per
                  confrontare il costo (netto) con il prezzo netto. La somministrazione di
                  alimenti e bevande è in genere al 10%; vendita/asporto e prestazioni separate
                  possono avere aliquote diverse. Usa sulla singola ricetta o voce l’aliquota
                  validata dal commercialista.
                </div>
              </div>
              )}

              {/* ── T-130: per i ruoli operativi resta una card autonoma; nella
                  vista consulente è una sotto-sezione del Magazzino. ── */}
              {isSettingsItemVisible('settings.recipe-qty') && !isConsultantOrOwner && (
              <div id="recipe-qty" style={{
                background: '#fff', borderRadius: 14, padding: '12px 14px',
                boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                marginBottom: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Quantità ricette
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: recipeQtyEnabled ? '#15803d' : '#94a3b8', marginTop: 2 }}>
                      {recipeQtyEnabled ? 'Attive — inserisci le dosi' : 'Disattive — solo ingredienti'}
                    </div>
                  </div>
                  <IonToggle
                    checked={recipeQtyEnabled}
                    disabled={togglingRecipeQty || warehouseEnabled}
                    onIonChange={(ev) => {
                      const next = !!ev.detail.checked;
                      if (next === recipeQtyEnabled) return;
                      setRecipeQtyConfirm(next);
                    }}
                  />
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, lineHeight: 1.4 }}>
                  {warehouseEnabled
                    ? 'Con il magazzino attivo le quantità sono obbligatorie. Disattiva prima il magazzino per cambiarle.'
                    : 'Se disattive, nell’editor ricette non si inseriscono le dosi (solo gli ingredienti). I dati già inseriti restano salvati.'}
                </div>
              </div>
              )}

              {/* ── T-291: TEMPERATURA DI RICEVIMENTO PER ARTICOLO ──
                  Config consulente/owner: quando ON, il Carico DDT/fattura chiede
                  la temperatura di ricevimento per ogni articolo (oltre alla
                  conformità generale). Finisce nel MOD 01 (export-carico-month). */}
              {isConsultantOrOwner && consultantSection === 'modules' && (
              <div id="receiving-temp" style={{
                background: '#fff', borderRadius: 14, padding: '12px 14px',
                boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                marginBottom: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Temperatura al ricevimento (per articolo)
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: receivingTempEnabled ? '#15803d' : '#94a3b8', marginTop: 2 }}>
                      {receivingTempEnabled ? 'Attiva — chiesta al Carico DDT' : 'Off — solo conformità del documento'}
                    </div>
                  </div>
                  <IonToggle
                    checked={receivingTempEnabled}
                    disabled={togglingReceivingTemp}
                    onIonChange={(ev) => { void onToggleReceivingTemp(!!ev.detail.checked); }}
                  />
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, lineHeight: 1.4 }}>
                  Quando attiva, in fase di carico DDT/fattura si registra la temperatura
                  rilevata per ogni alimento (check HACCP di ricevimento). Compare nella
                  colonna “Conf. ric.” dell’export MOD 01.
                </div>
              </div>
              )}

              {/* ── SINCRONIZZAZIONE ── */}
              <div style={{
                display: !isConsultantOrOwner || consultantSection === 'app' ? 'block' : 'none',
                background: '#fff',
                borderRadius: 14,
                padding: '12px 14px',
                boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                marginBottom: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: 4, flexShrink: 0,
                      background: syncDotColor,
                      boxShadow: sync.status === 'pulling' || sync.status === 'pushing' ? `0 0 0 4px ${syncDotColor}33` : 'none',
                      animation: sync.status === 'pulling' || sync.status === 'pushing' ? 'pulse 1.5s ease-in-out infinite' : 'none',
                    }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                        {attentionCount > 0 ? 'Sincronizzazione da verificare' : sync.status === 'idle' && sync.pendingCount > 0 ? 'Sincronizzazione incompleta' : t(`sync.${sync.status}`)}
                        {sync.pendingCount > 0 && (
                          <span style={{ marginLeft: 6, fontSize: 11, color: '#b45309', fontWeight: 700 }}>
                            ·{' '}{sync.pendingCount} in coda
                          </span>
                        )}
                        {(sync.reconciliationCount ?? 0) > 0 && (
                          <span style={{ marginLeft: 6, fontSize: 11, color: '#b45309', fontWeight: 700 }}>
                            · {sync.reconciliationCount} da verificare
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                        Ultimo contatto: {sync.lastPullAt ? new Date(sync.lastPullAt).toLocaleString('it-IT', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : 'non ancora rilevato'}
                      </div>
                    </div>
                  </div>
                  <IonButton
                    size="small"
                    fill="clear"
                    disabled={sync.status === 'pulling' || sync.status === 'pushing'}
                    onClick={async () => {
                      try {
                        const { getSyncEngine } = await import('../../lib/sync/engine.js');
                        await getSyncEngine().tick();
                      } catch (e) {
                        console.error('[sync] manual tick failed', e);
                      }
                    }}
                    style={{ '--border-radius': '10px', textTransform: 'none', flexShrink: 0 } as React.CSSProperties}
                  >
                    {sync.status === 'pulling' || sync.status === 'pushing' ? 'In corso' : 'Sincronizza'}
                  </IonButton>
                </div>
                {attentionCount > 0 && (
                  <div role="status" style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8,
                    background: '#fef3c7', color: '#92400e', fontSize: 12 }}>
                    {attentionCount} {attentionCount === 1 ? 'operazione richiede' : 'operazioni richiedono'} una verifica,
                    ma {attentionCount === 1 ? 'è isolata' : 'sono isolate'} dalle altre sincronizzazioni
                    e non {attentionCount === 1 ? 'blocca' : 'bloccano'} il lavoro sul tablet.
                    I dati sono conservati su questo dispositivo.
                    {isPlatformOwner && <IonButton size="small" fill="clear" routerLink="/settings/diagnostics">Verifica coda</IonButton>}
                  </div>
                )}
                {sync.status === 'error' && sync.lastError && (
                  <div style={{
                    marginTop: 8, padding: '8px 10px', borderRadius: 8,
                    background: '#fee2e2', color: '#b91c1c',
                    fontSize: 11, whiteSpace: 'pre-wrap',
                  }}>
                    {sync.lastError}
                  </div>
                )}
                {(sync.reconciliationCount ?? 0) > 0 && (
                  <div style={{
                    marginTop: 8, padding: '8px 10px', borderRadius: 8,
                    background: '#fef3c7', color: '#92400e', fontSize: 11,
                  }}>
                    Le operazioni non valide sono state isolate: la sincronizzazione
                    continua. Apri Magazzino per vedere cosa deve essere verificato.
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        <div style={{
          display: !isConsultantOrOwner || consultantSection === 'app' ? 'block' : 'none',
          padding: '12px 16px',
        }}>
          <h2 style={{ margin: '8px 4px 12px', fontSize: 14, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Configurazione avanzata
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {/* T-011: 'Audit & Compliance' e 'Diagnostica' sono strumenti
                interni di piattaforma — visibili al solo platform owner.
                Tutte le altre voci restano visibili al ristoratore. */}
            {(([
              { icon: '🏢', label: 'Dati locale', href: '/settings/business', ownerOnly: false, cfgKey: 'settings.business' as string | undefined },
              // T-076: "Menù" spostato nella dashboard (TodayPage). Mantenuto
              // in /settings/menu come route, ma niente più entry qui per
              // evitare doppione.
              // T-215: anagrafica destinazioni pasti (solo modulo collettiva).
              // T-403: spostata nella dashboard come tile "Destinazioni"
              // (decisione owner 2026-07-02) — la route resta, niente entry qui.
              { icon: '👥', label: 'Personale', href: '/settings/staff', ownerOnly: false, cfgKey: 'settings.staff' as string | undefined },
              { icon: '🖨️', label: 'Stampante', href: '/settings/printer', ownerOnly: false, cfgKey: 'settings.printer' as string | undefined },
              // T-148: tile "Abbattimento" rimossa — l'abbattimento si configura
              // ora SOLO nella ricetta (sorgente unica) e si vede in tracciabilità.
              { icon: '📊', label: 'Report ASL', href: '/settings/reports', ownerOnly: false, cfgKey: 'settings.reports' as string | undefined },
              { icon: '📸', label: 'Prove operative', href: '/settings/operational-evidence', ownerOnly: false, managerOnly: true, cfgKey: undefined as string | undefined },
              { icon: '🔒', label: 'Audit & Compliance', href: '/settings/audit', ownerOnly: true, cfgKey: undefined as string | undefined },
              { icon: '🧾', label: 'SDI e-fatture', href: '/settings/sdi', ownerOnly: false, cfgKey: 'settings.sdi' as string | undefined },
              { icon: '🐞', label: 'Diagnostica', href: '/settings/diagnostics', ownerOnly: true, cfgKey: undefined as string | undefined },
            ] as const)
              .filter((nav) => isPlatformOwner || !nav.ownerOnly)
              .filter((nav) => !('managerOnly' in nav) || !nav.managerOnly || operationalIdentity.canManageOperationalWork)
              .filter((nav) => !nav.cfgKey || isSettingsItemVisible(nav.cfgKey))
            ).map((nav) => (
              <IonButton
                key={nav.href}
                expand="block"
                fill="solid"
                color="light"
                routerLink={nav.href}
                style={{
                  '--background': '#ffffff',
                  '--color': '#0f172a',
                  '--border-radius': '12px',
                  '--box-shadow': '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                  '--padding-top': '14px',
                  '--padding-bottom': '14px',
                  margin: 0,
                  textTransform: 'none',
                } as React.CSSProperties}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 22 }}>{nav.icon}</span>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{nav.label}</span>
                </div>
              </IonButton>
            ))}
          </div>
        </div>

        {/* ── AZIONI FINALI ── lista verticale con icone */}
        <div style={{
          display: !isConsultantOrOwner || consultantSection === 'app' ? 'block' : 'none',
          padding: '0 16px 24px',
        }}>
          <h2 style={{ margin: '8px 4px 12px', fontSize: 14, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Account
          </h2>
          <div style={{
            background: '#fff',
            borderRadius: 14,
            boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
            overflow: 'hidden',
          }}>
            {([
              {
                icon: '💬',
                label: 'Assistenza',
                hint: 'Scrivici un\'email',
                onClick: () => {
                  const subject = encodeURIComponent('TrackEAT — richiesta supporto');
                  window.location.href = `mailto:support@trackeat.eu?subject=${subject}`;
                },
                danger: false,
              },
              {
                icon: '📜',
                label: 'Privacy Policy',
                hint: 'trackeat.eu/privacy',
                onClick: () => { window.open('https://trackeat.eu/privacy', '_blank', 'noopener'); },
                danger: false,
              },
              {
                icon: '📄',
                label: 'Termini di Servizio',
                hint: 'trackeat.eu/terms',
                onClick: () => { window.open('https://trackeat.eu/terms', '_blank', 'noopener'); },
                danger: false,
              },
              {
                icon: '📋',
                label: 'Validità registri HACCP',
                hint: 'Perché i registri digitali sono validi',
                onClick: () => { setComplianceOpen(true); },
                danger: false,
              },
              ...(sharedTabletOperator.isSharedTablet
                ? [
                    {
                      icon: <IonIcon icon={lockClosedOutline} aria-hidden="true" style={{ fontSize: 20 }} />,
                      label: 'Blocca e cambia operatore',
                      hint: `Operatore attivo: ${sharedTabletOperator.activeOperator?.fullName ?? 'nessuno'}`,
                      onClick: () => { sharedTabletOperator.changeOperator(); },
                      danger: false,
                    },
                    {
                      icon: <IonIcon icon={logOutOutline} aria-hidden="true" style={{ fontSize: 20 }} />,
                      label: 'Esci dall’account del tablet',
                      hint: 'Torna alla schermata email e password',
                      onClick: () => { void sharedTabletOperator.exitAccount(); },
                      danger: true,
                    },
                  ]
                : [{
                    icon: <IonIcon icon={logOutOutline} aria-hidden="true" style={{ fontSize: 20 }} />,
                    label: t('auth.signout'),
                    hint: 'Disconnetti questo dispositivo',
                    onClick: () => { void sharedTabletOperator.exitAccount(); },
                    danger: true,
                  }]),
            ] as const).map((row, idx, arr) => (
              <div
                key={row.label}
                onClick={row.onClick}
                role="button"
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '14px 16px',
                  borderBottom: idx < arr.length - 1 ? '1px solid #f1f5f9' : 'none',
                  cursor: 'pointer',
                }}
              >
                <div style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: row.danger ? '#fee2e2' : '#f1f5f9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, flexShrink: 0,
                }}>
                  {row.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: row.danger ? '#b91c1c' : '#0f172a' }}>
                    {row.label}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                    {row.hint}
                  </div>
                </div>
                <span style={{ color: '#cbd5e1', fontSize: 18, flexShrink: 0 }}>›</span>
              </div>
            ))}
          </div>
          {/* Footer brand */}
          <div style={{ textAlign: 'center', marginTop: 24, fontSize: 11, color: '#94a3b8' }}>
            TrackEAT · v{updateCheck?.current ?? installedVersion ?? '0.1.x'}
          </div>
        </div>
        <IonToast
          isOpen={!!toast}
          message={toast ?? ''}
          duration={2500}
          onDidDismiss={() => setToast(null)}
        />

        {/* T-073: conferma eliminazione locale. Doppia conferma per evitare
            tap accidentali sul bottone "🗑️" — l'azione è soft (archived_at)
            ma comunque rimuove il locale dal switcher, quindi serve un attimo
            di pausa cognitiva. */}
        <IonAlert
          isOpen={deleteBusinessOpen}
          onDidDismiss={() => setDeleteBusinessOpen(false)}
          header="Eliminare il locale?"
          message={(() => {
            const activeBiz = tenantBusinesses.find((b) => b.id === profile?.active_business_id);
            const name = activeBiz?.name ?? 'questo locale';
            return `"${name}" non comparirà più nel selettore. I dati storici (produzioni, etichette, eventi HACCP) restano per audit.`;
          })()}
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            {
              text: 'Elimina',
              role: 'destructive',
              handler: () => { void archiveActiveBusiness(); },
            },
          ]}
        />
        {/* T-130: conferma su ogni cambio del flag "Quantità ricette" (I3). */}
        <IonAlert
          isOpen={recipeQtyConfirm !== null}
          onDidDismiss={() => setRecipeQtyConfirm(null)}
          header={recipeQtyConfirm ? 'Attivare le quantità nelle ricette?' : 'Disattivare le quantità nelle ricette?'}
          message={recipeQtyConfirm
            ? 'Nell’editor ricette comparirà il campo quantità per ogni ingrediente e semilavorato.'
            : 'Nell’editor ricette le quantità non verranno più chieste (solo gli ingredienti). I dati già inseriti restano salvati nel database.'}
          buttons={[
            { text: 'Annulla', role: 'cancel', handler: () => setRecipeQtyConfirm(null) },
            {
              text: 'Conferma',
              handler: () => {
                const next = recipeQtyConfirm;
                setRecipeQtyConfirm(null);
                if (next === null) return;
                setRecipeQtyEnabled.mutate(next, {
                  onSuccess: () => setToast(next ? 'Quantità ricette attivate' : 'Quantità ricette disattivate'),
                  onError: (e) => setToast(toUserMessage(e)),
                });
              },
            },
          ]}
        />
        <ComplianceDocModal isOpen={complianceOpen} onClose={() => setComplianceOpen(false)} />
      </IonContent>
    </IonPage>
  );
}
